import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import { useTheme } from '@mui/material/styles';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import axios from '../config/axiosConfig';

const PortfolioNews = ({ holdings = [] }) => {
    const theme = useTheme();
    const [news, setNews] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);

    const symbols = [...new Set(holdings.map(h => h.symbol))];
    const symbolsKey = symbols.join(',');

    const fetchNews = useCallback(async (pageNum = 1, append = false) => {
        if (symbols.length === 0) return;

        setLoading(true);
        setError(null);

        try {
            const response = await axios.get('/api/news/portfolio', {
                params: {
                    symbols: symbols.join(','),
                    page: pageNum,
                    limit: 8,
                    days: 7
                }
            });

            const { data, pagination } = response.data;

            if (append) {
                setNews(prev => [...prev, ...data]);
            } else {
                setNews(data);
            }
            setHasMore(pagination.hasMore);
            setPage(pageNum);
        } catch (err) {
            console.error('Error fetching portfolio news:', err);
            setError('Failed to load news');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [symbolsKey]);

    useEffect(() => {
        fetchNews(1, false);
    }, [symbolsKey, fetchNews]);

    const handleLoadMore = () => {
        fetchNews(page + 1, true);
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: '2-digit'
        });
    };

    if (symbols.length === 0) {
        return null;
    }

    // Group news by date
    const groupedNews = {};
    news.forEach(item => {
        const dateKey = formatDate(item.publishedDate);
        if (!groupedNews[dateKey]) {
            groupedNews[dateKey] = [];
        }
        groupedNews[dateKey].push(item);
    });

    const dateKeys = Object.keys(groupedNews);

    return (
        <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button
                    variant="contained"
                    size="small"
                    sx={{
                        borderRadius: '20px',
                        textTransform: 'none',
                        px: 2
                    }}
                >
                    News
                </Button>
            </Box>

            <Box
                sx={{
                    bgcolor: theme.palette.background.paper,
                    borderRadius: 1,
                    p: 2,
                    boxShadow: 10
                }}
            >
                {loading && news.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : error ? (
                    <Typography color="error" sx={{ py: 2 }}>
                        {error}
                    </Typography>
                ) : news.length === 0 ? (
                    <Typography sx={{ py: 2, color: theme.palette.text.secondary }}>
                        No news found for your holdings in the last 7 days.
                    </Typography>
                ) : (
                    <Box>
                        {dateKeys.map(dateKey => (
                            <Box key={dateKey} sx={{ mb: 3 }}>
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        color: theme.palette.text.secondary,
                                        mb: 1,
                                        fontWeight: 600
                                    }}
                                >
                                    {dateKey}
                                </Typography>

                                {groupedNews[dateKey].map((item, index) => (
                                    <NewsItem
                                        key={item._id || index}
                                        item={item}
                                        isLast={index === groupedNews[dateKey].length - 1}
                                        formatTime={formatTime}
                                        theme={theme}
                                    />
                                ))}
                            </Box>
                        ))}

                        {hasMore && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                <Button
                                    onClick={handleLoadMore}
                                    disabled={loading}
                                    variant="outlined"
                                    size="small"
                                >
                                    {loading ? 'Loading...' : 'Load More'}
                                </Button>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

const NewsItem = ({ item, isLast, formatTime, theme }) => {
    const borderStyle = isLast ? 'none' : '1px solid ' + theme.palette.divider;

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                py: 1.5,
                borderBottom: borderStyle
            }}
        >
            <Typography
                variant="caption"
                sx={{
                    color: theme.palette.text.secondary,
                    minWidth: '70px',
                    flexShrink: 0,
                    mt: 0.3
                }}
            >
                {formatTime(item.publishedDate)}
            </Typography>

            <Box sx={{ flex: 1, ml: 2, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <RouterLink
                    to={'/asset/' + item.symbol}
                    style={{
                        color: theme.palette.primary.main,
                        textDecoration: 'none',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        flexShrink: 0
                    }}
                >
                    {item.symbol}
                </RouterLink>

                <Typography
                    component="span"
                    variant="body2"
                    sx={{
                        color: theme.palette.text.primary,
                        flex: 1,
                        lineHeight: 1.4
                    }}
                >
                    {item.title}
                </Typography>

                {item.url && (
                    <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            color: theme.palette.primary.main,
                            flexShrink: 0
                        }}
                    >
                        <OpenInNewIcon sx={{ fontSize: 14 }} />
                    </a>
                )}
            </Box>
        </Box>
    );
};

export default PortfolioNews;